import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import allowanceIncidences from 'modules/timeoff/store/collections/allowanceIncidences'
import allowances from 'modules/timeoff/store/collections/allowances'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import medicalLeaveUpdates from 'modules/timeoff/store/collections/medicalLeaveUpdates'
import policies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

// Function to load essential data
const fetchEssentialData = (
  employeeId: string,
  employeeTimeoffPolicyId: string | null | undefined
) => {
  const allowancesData = hasPolicyTemporality()
    ? {}
    : { timeoff_policy_id: employeeTimeoffPolicyId }

  const essentialCollections = [
    {
      target: allowances,
      data: allowancesData,
    },
    {
      target: policies,
      data: {
        employee_id: employeeId,
      },
    },
    {
      target: policyTimelines,
      data: {
        employee_id: employeeId,
      },
    },
  ]

  return Promise.all(
    essentialCollections.map((info) =>
      info.target.fetch({
        data: info.data,
      })
    )
  )
}

// Function to load secondary data
const fetchSecondaryData = (employeeId: string, employeeLocationId: string | undefined | null) => {
  const secondaryCollections = [
    {
      target: allowanceIncidences,
      data: {
        employee_ids: [employeeId],
      },
    },
    {
      target: companyHolidays,
      data: employeeLocationId ? { location_id: employeeLocationId } : {},
    },
    {
      target: leaveTypes,
      data: {},
    },
    {
      target: medicalLeaveUpdates,
      data: {
        employee_id: employeeId,
      },
    },
  ]

  return Promise.all(
    secondaryCollections.map((info) =>
      info.target.fetch({
        data: info.data,
      })
    )
  )
}

export const useFetchData = (
  employeeId: string,
  employeeTimeoffPolicyId: string | null | undefined,
  employeeLocationId: string | undefined | null,
  year: number
) => {
  const { fetchData } = useLeaves()

  // Essential data loading
  const { loading } = useAsyncData(
    () =>
      Promise.all([
        fetchEssentialData(employeeId, employeeTimeoffPolicyId),
        fetchSecondaryData(employeeId, employeeLocationId),
        fetchData(employeeId),
      ]),
    [employeeId, year]
  )

  return {
    loading,
  }
}
